// URL builders (spec/dashboard.md section 3.1): the URLs mirror the API's tenancy path.
const e = encodeURIComponent;

export const routes = {
  orgs: () => '/orgs',
  games: (org: string) => `/orgs/${e(org)}/games`,
  game: (org: string, game: string) => `/orgs/${e(org)}/games/${e(game)}`,
  branch: (org: string, game: string, branch: string, page = 'templates') => `/orgs/${e(org)}/games/${e(game)}/branches/${e(branch)}/${page}`,
  templates: (org: string, game: string, branch: string) => routes.branch(org, game, branch, 'templates'),
  template: (org: string, game: string, branch: string, template: string) => routes.branch(org, game, branch, `templates/${e(template)}`),
  documents: (org: string, game: string, branch: string, template: string) => routes.branch(org, game, branch, `templates/${e(template)}/documents`),
  templateNew: (org: string, game: string, branch: string) => routes.branch(org, game, branch, 'templates/new'),
  enums: (org: string, game: string, branch: string) => routes.branch(org, game, branch, 'enums'),
  enumEditor: (org: string, game: string, branch: string, name: string) => routes.branch(org, game, branch, `enums/${e(name)}`),
  enumNew: (org: string, game: string, branch: string) => routes.branch(org, game, branch, 'enums/new'),
  profiles: (org: string, game: string, branch: string) => routes.branch(org, game, branch, 'profiles'),
  tags: (org: string, game: string, branch: string) => routes.branch(org, game, branch, 'tags'),
  /** the branch search page (section 6.6): `?q=&template=&tag=` */
  search: (org: string, game: string, branch: string, q?: string | null, filters: { template?: string | null; tag?: string | null } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (filters.template) params.set('template', filters.template);
    if (filters.tag) params.set('tag', filters.tag);
    const query = params.toString();
    return routes.branch(org, game, branch, 'search') + (query ? `?${query}` : '');
  },
  env: (org: string, game: string, env: string, page = 'players') => `/orgs/${e(org)}/games/${e(game)}/envs/${e(env)}/${page}`,
  /** the players pages (section 11): the search page, a player, an erasure receipt, the orders list (optionally filtered by player) */
  players: (org: string, game: string, env: string) => routes.env(org, game, env, 'players'),
  player: (org: string, game: string, env: string, player: string) => routes.env(org, game, env, `players/${e(player)}`),
  receipt: (org: string, game: string, env: string, receipt: string) => routes.env(org, game, env, `players/erasures/${e(receipt)}`),
  orders: (org: string, game: string, env: string, filters: { player?: string | null; paymentId?: string | null } = {}) => {
    const params = new URLSearchParams();
    if (filters.player) params.set('player', filters.player);
    if (filters.paymentId) params.set('paymentId', filters.paymentId);
    const query = params.toString();
    return routes.env(org, game, env, 'orders') + (query ? `?${query}` : '');
  },
  gamePage: (org: string, game: string, page: string) => `/orgs/${e(org)}/games/${e(game)}/${page}`,
  /** the slice 8 pages (section 12): history (a commit, a compare), releases (a tab), merge requests (one by number), the game settings (a tab), the organization pages */
  history: (org: string, game: string, branch: string, sha?: string | null) => routes.branch(org, game, branch, sha ? `history/${e(sha)}` : 'history'),
  compare: (org: string, game: string, branch: string, from: string, to: string) => `${routes.branch(org, game, branch, 'history')}?from=${e(from)}&to=${e(to)}`,
  releases: (org: string, game: string, branch: string, tab?: 'releases' | 'environments' | 'jobs') => routes.branch(org, game, branch, tab && tab !== 'releases' ? `releases/${tab}` : 'releases'),
  mergeRequests: (org: string, game: string, branch: string) => routes.branch(org, game, branch, 'merge-requests'),
  mergeRequest: (org: string, game: string, branch: string, number: number | string) => routes.branch(org, game, branch, `merge-requests/${e(String(number))}`),
  settings: (org: string, game: string, tab?: 'general' | 'members' | 'keys' | 'webhooks' | 'audit') => routes.gamePage(org, game, tab && tab !== 'general' ? `settings/${tab}` : 'settings'),
  orgPage: (org: string, page: 'games' | 'members' | 'keys' | 'integrations' | 'audit') => `/orgs/${e(org)}/${page}`,
  account: () => '/account',
  /** the caller's own audit rows (M7 slice 1; `GET /v1/me/audit`), the filters as query parameters */
  activity: () => '/account/activity',
  tokens: () => '/account/tokens',
  password: () => '/account/password',
};

export interface SidebarItem {
  key: string;
  label: string;
  /** a branch page (`branches/{branch}/<page>`), an environment page (`envs/{env}/<page>`) or a game page */
  scope: 'branch' | 'env' | 'game';
  page: string;
  help?: string;
}

export const controlItems: SidebarItem[] = [
  { key: 'history', label: 'History', scope: 'branch', page: 'history' },
  { key: 'releases', label: 'Releases', scope: 'branch', page: 'releases' },
  { key: 'merge-requests', label: 'Merge requests', scope: 'branch', page: 'merge-requests' },
  { key: 'templates', label: 'Data structure', scope: 'branch', page: 'templates' },
  { key: 'localization', label: 'Localization', scope: 'branch', page: 'localization/strings' },
  { key: 'assets', label: 'Assets', scope: 'branch', page: 'assets/files' },
  { key: 'products', label: 'Products', scope: 'branch', page: 'products' },
  { key: 'extensions', label: 'Extensions', scope: 'branch', page: 'extensions' },
  { key: 'platforms', label: 'Platforms', scope: 'env', page: 'platforms' },
  { key: 'env-settings', label: 'Environment', scope: 'env', page: 'settings' },
  { key: 'players', label: 'Players', scope: 'env', page: 'players' },
  { key: 'orders', label: 'Orders', scope: 'env', page: 'orders' },
  { key: 'settings', label: 'Settings', scope: 'game', page: 'settings' },
];

export const liveOpsItems: SidebarItem[] = [
  { key: 'calendar', label: 'Calendar', scope: 'branch', page: 'liveops/calendar' },
  { key: 'events', label: 'Game events', scope: 'branch', page: 'liveops/events' },
  { key: 'offers', label: 'Offers', scope: 'branch', page: 'liveops/offers' },
  { key: 'store', label: 'Game store', scope: 'branch', page: 'liveops/store' },
  { key: 'items', label: 'Items', scope: 'branch', page: 'liveops/items' },
  { key: 'store-items', label: 'Store items', scope: 'branch', page: 'liveops/store-items' },
  { key: 'daily-bonus', label: 'Daily bonus', scope: 'branch', page: 'liveops/daily-bonus' },
  { key: 'segments', label: 'Segmentation', scope: 'branch', page: 'liveops/segments' },
  { key: 'overrides', label: 'Overrides', scope: 'branch', page: 'liveops/overrides' },
  { key: 'ab-tests', label: 'A/B tests', scope: 'branch', page: 'liveops/ab-tests' },
  { key: 'reward-tables', label: 'Reward tables', scope: 'branch', page: 'liveops/reward-tables' },
  { key: 'sales', label: 'Sales', scope: 'branch', page: 'liveops/sales' },
];

/** Balancy's built-in namespaces: shown in the Documents section only when "all templates" is on. */
export const systemNamespaces = ['SmartObjects', 'LiveOps', 'Core', 'VisualScripting', 'Notifications', 'OfferGroupInfo'];

export function isSystemTemplate(name: string): boolean {
  const ns = name.split('.')[0]!;
  return systemNamespaces.includes(ns);
}
